/* eslint-disable react/no-danger, react/no-unescaped-entities, max-len, consistent-return */
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import track from 'react-tracking';
import classNames from 'classnames';
import { connect } from 'react-redux';
import {
  Alert, Container, Collapsible,
} from '@edx/paragon';
import {
  LibraryBooks, Person, AccessTime, CreditCard, Warning, School, FormatQuote,
  AddCircle, RemoveCircle, LocalOffer,
} from '@edx/paragon/icons';
import { v4 as uuidv4 } from 'uuid';
import { defineMessages, injectIntl, FormattedMessage } from 'gatsby-plugin-react-intl';

import {
  formatList,
  formatPricesForComparison,
  ListStyles,
  formatProgramPrice,
} from '@utils/formatting';
import { submitEvent } from '@utils/analytics';
import {
  getProgramDuration,
  getProgramPacing,
  getTotalEstimatedEffortInHoursPerWeek, programIsMicroMasters, programIsProfessionalCertificate,
} from '@utils/program';
import { defaultUserCurrency } from '@utils/i18n';
import { WebviewContext } from '@utils/context';

import CloudflareImage from '@prospectus/common/ui/CloudflareImage';

import BulletList from '@prospectus/common/ui/BulletList';
import FAQs from '@prospectus/common/ui/FAQs';
import ProgramPathway from '@prospectus/program-page/components/ProgramPathway';
import Video from '@prospectus/common/ui/Video';

import { currencyInfoShape } from '@prospectus/common/types/allCurrencyShape';
import programShape from '@prospectus/common/types/programShape';
import videoShape from '@prospectus/common/types/videoShape';
import { intlShape } from '@prospectus/common/types/intlShape';

import globalMessages from '@utils/globalMessages';
import { pageTrackingObjectUpdate } from '@utils/cohesion/helpers';
import ProgramSubscriptionSection from '../ProgramSubscriptionSection';
import Endorsement from './components/Endorsement';
import InstructorCard from './components/InstructorCard';
import ProgramTrackModal from './components/ProgramTrackModal';
import ProgramPursueButton from './components/ProgramPursueButton';

const getDiscountedPriceFormatting = (formattedPrice) => ({
  formattedPrice,
  markup: chunks => <span className="sr-only">{chunks}</span>,
});

const getOriginalPriceFormatting = (formattedOriginalPrice) => ({
  formattedOriginalPrice,
  markup: chunks => <span className="sr-only">{chunks}</span>,
  s: chunks => <s className="ml-2">{chunks}</s>,
});

const getEnrollContextFormatting = (courses, totalEstimatedDuration) => ({
  courseCount: courses.length,
  estimatedDuration: totalEstimatedDuration,
});

const getInstructorsHeadingFormatting = (owners, intl, staff) => ({
  providers: formatList(
    owners.map(({ name, key }) => (name === key ? name : `${name} (${key})`)),
    ListStyles.punctuation,
    intl,
  ),
  instructorCount: staff.length,
  markup1: chunks => <h2 className="h2 section-title mt-4">{chunks}</h2>,
  markup2: chunks => <span className="providers">{chunks}</span>,
  subtitle: chunks => <div className="card-group-subtitle ml-0">{chunks}</div>,
});

const messages = defineMessages({
  'prospectus.program.main.programOverview': {
    id: 'prospectus.program.main.programOverview',
    defaultMessage: 'Program Overview',
    description: 'Title for the program overview section',
  },
  'prospectus.program.main.subscription.faq.heading': {
    id: 'prospectus.program.main.subscription.faq.heading',
    defaultMessage: 'How does the program subscription work?',
    description: 'Title for the program subscription section',
  },
  'prospectus.program.main.instructors.view': {
    id: 'prospectus.program.main.instructors.view',
    defaultMessage: 'See instructor bios',
    description: 'When clicked, this link displays instructor biographies.',
  },
  'prospectus.program.main.instructor.show': {
    id: 'prospectus.program.main.instructor.show',
    defaultMessage: 'Show Biographies',
    description: 'This is the description for a link for biographies.',
  },
});

const staffPhotoSize = 50;

function BaseProgramMain(props) {
  const [trackModalOpen, setTrackModalOpen] = useState(false);
  const [emailOptInValue, setEmailOptInValue] = useState(false);

  const {
    authoringOrganizations: owners,
    corporateEndorsements: endorsements,
    creditRedemptionOverview: credentialInfo,
    title,
    type,
    courses,
    overview,
    programOriginalPrice,
    programPrice,
    staff,
    hasOfacRestrictions,
    expectedLearningItems: whatYouWillLearn,
    instructorOrdering,
    isProgramEligibleForOneClickPurchase: isBundled,
    faq,
    jobOutlookItems: jobOutlook,
    primarySubject,
    video,
    enrollButtonId,
    intl,
    userCurrency,
    creditValue,
    showPrice,
    courseFocusId,
    linkedProgram,
    retiringSoon,
    programDurationOverride,
    subscriptionDetails,
    isSubscriptionLoading,
    tracking,
  } = props;

  useEffect(() => {
    pageTrackingObjectUpdate('product-page');
  }, []);

  const handleProgramTrackModalEvent = (eventName) => {
    const componentProps = {
      category: 'navigation',
    };

    submitEvent(tracking, componentProps, eventName);
  };

  const handleExpandableSectionClick = (isOpen, label) => {
    const componentProps = {
      category: 'navigation',
      label,
      isOpen,
    };

    submitEvent(tracking, componentProps, 'edx.bi.experiment.expandable-section.clicked');
  };

  const getExpertInstructionSecondaryLabel = () => {
    if (programIsMicroMasters(type)) {
      return (
        <FormattedMessage
          id="prospectus.program.main.statistic.instruction.description.micromasters"
          defaultMessage="{courseCount} graduate-level courses"
          description="Description for the statistic describing expert instruction."
          values={{
            courseCount: courses.length,
          }}
        />
      );
    }
    if (programIsProfessionalCertificate(type)) {
      return (
        <FormattedMessage
          id="prospectus.program.main.statistic.instruction.description.professional_certificate"
          defaultMessage="{courseCount} skill-building courses"
          description="Description for the statistic describing expert instruction."
          values={{
            courseCount: courses.length,
          }}
        />
      );
    }
    return (
      <FormattedMessage
        id="prospectus.program.main.statistic.instruction.description.xseries"
        defaultMessage="{courseCount} high-quality courses"
        description="Description for the statistic describing expert instruction."
        values={{
          courseCount: courses.length,
        }}
      />
    );
  };

  const setAndFormatPriceData = () => {
    const parsedOriginalPrice = parseFloat(programOriginalPrice);
    const parsedPrice = parseFloat(programPrice);
    if (Number.isNaN(parsedOriginalPrice) || Number.isNaN(parsedPrice)) {
      // NaNs will cause formatPricesForComparison to throw an error, so short circuit and set to null
      return {
        formattedPrice: null,
        formattedOriginalPrice: null,
        priceCurrencyCode: null,
        priceIsDiscounted: false,
      };
    }
    return {
      ...formatPricesForComparison(programOriginalPrice, programPrice, userCurrency, intl),
      priceIsDiscounted: parsedOriginalPrice > parsedPrice,
    };
  };

  const openTrackModal = (value) => {
    handleProgramTrackModalEvent('edx.bi.program-track-modal.open');
    setTrackModalOpen(true);
    setEmailOptInValue(value);
  };

  const resetTrackModalState = () => {
    handleProgramTrackModalEvent('edx.bi.program-track-modal.closed');
    setTrackModalOpen(false);
  };

  const programStaff = instructorOrdering.length > 0 ? instructorOrdering : staff;
  const totalEstimatedDuration = programDurationOverride || getProgramDuration(courses, intl);
  const programPacingType = useMemo(() => getProgramPacing(courses), [courses]);

  const { isSubscriptionEnabled, subscriptionStatus, priceDetails } = subscriptionDetails;

  const hoursPerWeekFormatted = getTotalEstimatedEffortInHoursPerWeek(courses);

  const faqs = hasOfacRestrictions ? [{
    question: intl.formatMessage(globalMessages['prospectus.program.detail.main.ofac.question']),
    answer: intl.formatMessage(globalMessages['prospectus.program.detail.main.ofac.answer']),
    id: 'program-ofac',
  }, ...faq] : faq;

  const faqsWithUUID = Array.isArray(faqs) && faqs.length > 0 && faqs.map((faqItem) => ({ id: uuidv4(), ...faqItem }));

  const {
    formattedPrice,
    formattedOriginalPrice,
    priceCurrencyCode,
    priceIsDiscounted,
  } = setAndFormatPriceData();

  // Need to add a link in href once provided
  const subscriptionContent = '<p>A program subscription gives you full verified access to all courses and materials within the program you’ve enrolled in, for as long as your subscription is active. Monthly subscription pricing can help you manage your enrollment costs — instead of paying more up front, you pay a smaller amount per month for only as long as you need access. You can cancel your subscription at any time for no additional fee.</p>';

  // For program track selection. If there is a linkedProgram, get its
  // formatted price and add it to a copy of the linkedProgram object to be
  // passed to along to child components.
  const linkedProgramWithFormattedPrice = linkedProgram
    ? { ...linkedProgram, formattedPrice: setAndFormatPriceData().formattedPrice }
    : null;

  const getProgramPurchaseSection = () => {
    if (isSubscriptionEnabled) {
      return (
        <ProgramSubscriptionSection
          subscriptionDetails={subscriptionDetails}
          userState={subscriptionStatus}
          enrollButtonId={enrollButtonId}
          isSubscriptionLoading={isSubscriptionLoading}
          id={enrollButtonId}
          displayTrackModal={!!linkedProgram}
          trackModalClickHandler={openTrackModal}
          {...props}
        />
      );
    }
    return (
      <>
        <FormattedMessage
          id="prospectus.program.main.heading.enroll"
          defaultMessage="Enrolling Now"
          description="Heading for the program bundle enroll section."
        >{text => <h2 className="h2 section-title">{text}</h2>}
        </FormattedMessage>
        {showPrice && !priceIsDiscounted
            && (
              <div className="program-price d-flex flex-wrap">
                {formattedPrice}
                {priceCurrencyCode
                  && (
                    <div className="font-weight-normal">
                      <span className="ml-1">{priceCurrencyCode}</span>
                    </div>
                  )}
              </div>
            )}
        {showPrice && priceIsDiscounted
            && (
              <div className="program-price d-flex flex-wrap">
                <FormattedMessage
                  id="prospectus.program.main.enroll.price.discounted.screenreader.discounted"
                  description="Screenreader-only text for presenting discounted price of program"
                  defaultMessage="<markup>Discounted price:</markup> {formattedPrice}"
                  values={getDiscountedPriceFormatting(formattedPrice)}
                />
                <div className="font-weight-normal">
                  <FormattedMessage
                    id="prospectus.program.main.enroll.price.discounted.screenreader.original"
                    description="Screenreader-only text for presenting original price of discounted program"
                    defaultMessage="<markup>Pre-discounted price:</markup> <s>{formattedOriginalPrice}</s>"
                    values={getOriginalPriceFormatting(formattedOriginalPrice)}
                  />

                  {priceCurrencyCode
                    && <span className="ml-1">{priceCurrencyCode}</span>}
                </div>
              </div>
            )}
        <FormattedMessage
          id="prospectus.program.main.enroll.context"
          description="Context for the enroll button stating the number of courses and estimated duration."
          defaultMessage="{courseCount} courses in {estimatedDuration}"
          values={getEnrollContextFormatting(
            courses,
            totalEstimatedDuration,
          )}
        >{text => <div className="enroll-context">{text}</div>}
        </FormattedMessage>
        <WebviewContext.Consumer>
          {(webview) => (
            <ProgramPursueButton
              {...props}
              id={enrollButtonId}
              displayTrackModal={!!linkedProgram}
              trackModalClickHandler={openTrackModal}
              webview={webview}
            />
          )}
        </WebviewContext.Consumer>
      </>
    );
  };

  const getProgramPricingPlan = () => {
    const price = formatProgramPrice(priceDetails?.price);

    if (isSubscriptionEnabled) {
      return (
        <div className="program-stat">
          <div className="icon-circle">
            <LocalOffer />
          </div>
          <div className="details">
            <div className="main d-flex flex-wrap">
              <FormattedMessage
                id="prospectus.program.main.statistic.subscription.plan.price"
                description="Displaying the subscription payment"
                  // eslint-disable-next-line no-template-curly-in-string
                defaultMessage="${price}/month"
                values={{ price }}
              />
              <div className="font-weight-bold">
                <span className="ml-1">
                  <FormattedMessage
                    id="prospectus.program.main.statistic.subscription.plan.currency"
                    description="Displaying the subscription payment currency"
                    defaultMessage="{currency}"
                    values={{ currency: priceDetails?.currency }}
                  />
                </span>
              </div>
            </div>
            <div className="secondary">
              <FormattedMessage
                id="prospectus.program.main.statistic.subscription.price"
                description="Displaying the trial period"
                defaultMessage="After 7-day free trial"
              />
            </div>
          </div>
        </div>
      );
    }
    if (showPrice && formattedPrice) {
      return (
        <div className="program-stat">
          <div className="icon-circle">
            <CreditCard />
          </div>
          {!priceIsDiscounted
              && (
                <div className="details">
                  <div className="main d-flex flex-wrap">
                    {formattedPrice}
                    {priceCurrencyCode
                      && (
                        <div className="font-weight-normal">
                          <span className="ml-1">{priceCurrencyCode}</span>
                        </div>
                      )}
                  </div>
                  <div className="secondary">
                    <FormattedMessage
                      id="prospectus.program.main.statistic.price.nondiscounted.description"
                      description="Displayed below the price to briefly state that paying for the program gets access to everything."
                      defaultMessage="For the full program experience"
                    />
                  </div>
                </div>
              )}
          {priceIsDiscounted
              && (
                <div className="details">
                  <div className="main d-flex flex-wrap">
                    <FormattedMessage
                      id="prospectus.program.main.statistic.price.discounted.screenreader.discounted"
                      description="Screenreader-only text for presenting discounted price of program"
                      defaultMessage="<markup>Discounted price:</markup> {formattedPrice}"
                      values={getDiscountedPriceFormatting(formattedPrice)}
                    />
                    <div className="font-weight-normal">
                      <FormattedMessage
                        id="prospectus.program.main.statistic.price.discounted.screenreader.original"
                        description="Screenreader-only text for presenting original price of discounted program"
                        defaultMessage="<markup>Pre-discounted price:</markup> <s>{formattedOriginalPrice}</s>"
                        values={getOriginalPriceFormatting(formattedOriginalPrice)}
                      />
                      {priceCurrencyCode
                        && <span className="ml-1">{priceCurrencyCode}</span>}
                    </div>
                  </div>
                  <div className="secondary">
                    <FormattedMessage
                      id="prospectus.program.main.statistic.price.discounted.description"
                      description="Displayed below the price to briefly state that paying for the program gets access to everything."
                      defaultMessage="For the full program experience"
                    />
                  </div>
                </div>
              )}
        </div>
      );
    }
  };

  return (
    <>
      <Container size="lg" className="gradient-wrapper program-body">
        <Container size="lg">
          <Container size="lg" className="program-section">
            <div className="row">
              <div className="col-md-12 pb-3">
                {retiringSoon
                    && (
                      <Alert variant="warning" className="text-center">
                        <Warning className="mr-1" />
                        <FormattedMessage
                          id="prospectus.program.detail.retirementAlert"
                          description="Warning banner text signifying a course will be retiring soon"
                          defaultMessage="This program is retiring soon"
                        />
                      </Alert>
                    )}
              </div>
            </div>
            <div className="row">
              <div className="col-lg-8 mb-4">
                {whatYouWillLearn && whatYouWillLearn.length > 0
                    && (
                      <>
                        <FormattedMessage
                          id="prospectus.program.main.heading.whatYouWillLearn"
                          defaultMessage="What you will learn"
                          description="Heading for the what you will learn section."
                        >{text => <h2 className="h2 mb-3 section-title">{text}</h2>}
                        </FormattedMessage>
                        <BulletList items={whatYouWillLearn} />
                      </>
                    )}
                {video && video.src
                    && (
                      <Video
                        video={video}
                        title={title}
                      />
                    )}
                <Collapsible
                  title={(
                    <span className="h3">
                      {intl.formatMessage(messages['prospectus.program.main.programOverview'])}
                    </span>
                  )}
                  onToggle={isOpen => (
                    handleExpandableSectionClick(
                      isOpen,
                      intl.formatMessage(messages['prospectus.program.main.programOverview']),
                    )
                  )}
                  className="p-2"
                >
                  <div className="overview-info" dangerouslySetInnerHTML={{ __html: overview }} />
                </Collapsible>
                {isSubscriptionEnabled && (
                  <Collapsible
                    title={intl.formatMessage(messages['prospectus.program.main.subscription.faq.heading'])}
                    onToggle={isOpen => (
                      handleExpandableSectionClick(
                        isOpen,
                        intl.formatMessage(messages['prospectus.program.main.subscription.faq.heading']),
                      )
                    )}
                    className="pb-3.5"
                  >
                    <div
                      className="overview-info"
                      dangerouslySetInnerHTML={{ __html: subscriptionContent }}
                    />
                  </Collapsible>
                )}
              </div>
              <div className="col-lg-4">
                <div className="flex-column">
                  <div className="program-stat">
                    <div className="icon-circle">
                      <LibraryBooks />
                    </div>
                    <div className="details">
                      <div className="main">
                        <FormattedMessage
                          id="prospectus.program.main.statistic.instruction.title"
                          defaultMessage="Expert instruction"
                          description="Title for the statistic describing expert instruction."
                        />
                      </div>
                      <div className="secondary">
                        {getExpertInstructionSecondaryLabel()}
                      </div>
                    </div>
                  </div>
                  {programPacingType === 'self_paced'
                      && (
                        <div className="program-stat">
                          <div className="icon-circle">
                            <Person />
                          </div>
                          <div className="details">
                            <div className="main">
                              <FormattedMessage
                                id="prospectus.program.main.statistic.selfPaced.title"
                                defaultMessage="Self-paced"
                                description="Title for the statistic describing self paced learning."
                              />
                            </div>
                            <div className="secondary">
                              <FormattedMessage
                                id="prospectus.program.main.statistic.selfPaced.description"
                                defaultMessage="Progress at your own speed"
                                description="Description for the statistic describing self paced learning."
                              />
                            </div>
                          </div>
                        </div>
                      )}
                  {programPacingType === 'instructor_paced'
                      && (
                        <div className="program-stat">
                          <div className="icon-circle">
                            <Person />
                          </div>
                          <div className="details">
                            <div className="main">
                              <FormattedMessage
                                id="prospectus.program.main.statistic.instructorLed.title"
                                defaultMessage="Instructor-led"
                                description="Title for the statistic describing instructor led learning."
                              />
                            </div>
                            <div className="secondary">
                              <FormattedMessage
                                id="prospectus.program.main.statistic.instructorLed.description"
                                defaultMessage="Assignments and exams have specific due dates"
                                description="Description for the statistic describing instructor led learning."
                              />
                            </div>
                          </div>
                        </div>
                      )}
                  {totalEstimatedDuration && hoursPerWeekFormatted
                      && (
                        <div className="program-stat">
                          <div className="icon-circle">
                            <AccessTime />
                          </div>
                          <div className="details">
                            <div className="main">{totalEstimatedDuration}</div>
                            <div className="secondary">
                              <FormattedMessage
                                id="prospectus.program.main.statistic.averageEffort.description"
                                defaultMessage="{totalHours} hours per week"
                                description="Description for the statistic describing average hours of effort per week."
                                values={{
                                  totalHours: hoursPerWeekFormatted,
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                  {creditValue > 0
                      && (
                        <div className="program-stat">
                          <div className="icon-circle">
                            <School />
                          </div>
                          <div className="details">
                            <div className="main">
                              <FormattedMessage
                                id="prospectus.program.main.statistic.creditValue.title"
                                defaultMessage="{creditValue, plural, one {# Academic Credit} other {# Academic Credits}}"
                                description="A title containing information about the amount of actual credits a student would earn when completing this program"
                                values={{ creditValue }}
                              />
                            </div>
                            <div className="secondary">
                              <FormattedMessage
                                id="prospectus.program.main.statistic.creditValue.description"
                                defaultMessage="Build your skills and your transcript"
                                description="A small tag line about how earning academic credits can help the learner"
                              />
                            </div>
                          </div>
                        </div>
                      )}
                  {getProgramPricingPlan()}
                </div>
              </div>
            </div>
          </Container>
          <Container size="lg" className="program-section pb-0">
            <FormattedMessage
              id="prospectus.program.main.heading.coursesInThisProgram"
              defaultMessage="Courses in this program"
              description="Heading for the courses in this program section."
            >{text => <h2 className="h2 section-title" tabIndex="-1" id={courseFocusId}>{text}</h2>}
            </FormattedMessage>
          </Container>
          <Container size="lg" className="program-section pr-0 pl-0">
            <div className="row no-gutters">
              <div className="col-lg-8">
                <ProgramPathway
                  title={title}
                  type={type}
                  owners={owners}
                  programCourses={courses}
                  showCoursePacing={programPacingType === 'mixed'}
                  credentialInfo={credentialInfo}
                  jobOutlook={jobOutlook}
                  isBundled={isBundled}
                  linkedProgramCourses={linkedProgram && linkedProgram.courses}
                />
              </div>
            </div>
          </Container>
        </Container>
      </Container>
      <Container size="lg" className="pt-4 mt-4">
        <div className="program-section">
          <FormattedMessage
            id="prospectus.program.main.heading.instructors"
            description="Heading for the program instructors section."
            defaultMessage="<markup1>Meet your
                                {instructorCount, plural,
                                  one {instructor}
                                  other {instructors}
                                }
                                <subtitle>from <markup2>{providers}</markup2></subtitle></markup1>"
            values={getInstructorsHeadingFormatting(
              owners,
              intl,
              staff,
            )}
          />
          <div className="row program-staff">
            {programStaff.length > 4
                && (
                  <div className="col-md-8">
                    <div className="d-flex flex-wrap all-the-people">
                      {programStaff.map(person => (
                        <div className="instructor-image-wrapper overlay overlap" key={person.uuid}>
                          <CloudflareImage
                            className="rounded-circle w-100"
                            title={`${person.givenName} ${person.familyName}`}
                            url={person.profileImageUrl}
                            width={staffPhotoSize}
                            height={staffPhotoSize}
                          />
                        </div>
                      ))}
                    </div>
                    <Collapsible.Advanced
                      onToggle={isOpen => handleExpandableSectionClick(isOpen, intl.formatMessage(messages['prospectus.program.main.instructor.show']))}
                      className="ml-3 mb-4.5 mt-2.5"
                    >
                      <Collapsible.Trigger className="d-flex align-items-center text-black">
                        <span className="text-decoration-underline font-weight-bold mr-2">
                          {intl.formatMessage(messages['prospectus.program.main.instructors.view'])}
                        </span>
                        <Collapsible.Visible whenClosed><AddCircle /></Collapsible.Visible>
                        <Collapsible.Visible whenOpen><RemoveCircle /></Collapsible.Visible>
                      </Collapsible.Trigger>
                      <Collapsible.Body className="mt-4.5">
                        <div className="instructor-list no-gutters">
                          {programStaff.map(data => (<InstructorCard {...data} key={data.uuid} classList="col-lg-6 instructor" />))}
                        </div>
                      </Collapsible.Body>

                    </Collapsible.Advanced>
                  </div>
                )}
            {programStaff.length <= 4
                && (
                  <div className="col-md-8">
                    <div className="instructor-list no-gutters">
                      {programStaff.map(data => (<InstructorCard {...data} key={data.uuid} classList="col-lg-6 instructor" />))}
                    </div>
                  </div>
                )}
            <div className="col-md-4">
              <p className="text-callout">
                <FormattedMessage
                  id="prospectus.program.main.instructor.callout"
                  description="A callout highlighting the authoring organisations and their commitment."
                  defaultMessage="Experts from {authoringOrgs} committed to teaching online learning"
                  values={{
                    authoringOrgs: formatList(
                      owners.map(owner => owner.key),
                      ListStyles.punctuation,
                      intl,
                    ),
                  }}
                >{text => <span className="highlight">{text}</span>}
                </FormattedMessage>
              </p>
            </div>
          </div>
        </div>
        {endorsements && endorsements.length > 0
            && (
              <div className="program-section endorsements">
                <FormattedMessage
                  id="prospectus.program.main.heading.programEndorsements"
                  defaultMessage="Program endorsements"
                  description="Heading for the program endorsements section."
                >{text => <h2 className="h2 mb-3 section-title">{text}</h2>}
                </FormattedMessage>
                <div className="quote-icon-wrapper">
                  <FormatQuote height="48" width="48" />
                </div>
                <div className="row">
                  {endorsements.map(endorsement => <Endorsement {...endorsement} key={endorsement.corporationName} />)}
                </div>
              </div>
            )}
      </Container>
      {showPrice
          && isBundled
          && (
            <div className={
              classNames(
                'container mw-lg',
                'program-section',
                'd-flex',
                { 'align-items-center': !isSubscriptionEnabled },
                { 'py-5': isSubscriptionEnabled },
                'flex-column',
                'mb-0',
                'program-enroll-wrapper',
                primarySubject.slug,
              )
            }
            >
              {getProgramPurchaseSection()}
              {linkedProgram
                && (
                  <ProgramTrackModal
                    {...props}
                    formattedPrice={formattedPrice}
                    linkedProgram={linkedProgramWithFormattedPrice}
                    open={trackModalOpen}
                    onClose={resetTrackModalState}
                    parentSelector=".program-pursue-btn"
                    passedEmailOptIn={emailOptInValue}
                  />
                )}
            </div>
          )}
      {faqsWithUUID && faqsWithUUID.length > 0
          && (
            <Container size="lg" className="pt-4">
              <Container size="lg">
                <div className="program-section col-lg-8 p-0">
                  <FormattedMessage
                    id="prospectus.program.main.heading.faqs"
                    defaultMessage="FAQs"
                    description="Heading for the frequently asked questions section."
                  >{text => <h2 className="h2 section-title">{text}</h2>}
                  </FormattedMessage>
                  <FAQs questions={faqsWithUUID} />
                </div>
              </Container>
            </Container>
          )}
    </>
  );
}

BaseProgramMain.propTypes = {
  title: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  faq: PropTypes.arrayOf(PropTypes.shape({
    question: PropTypes.string,
    answer: PropTypes.string,
  })),
  courses: PropTypes.arrayOf(PropTypes.shape({
    activeCourseRun: PropTypes.shape(),
  })).isRequired,
  linkedProgram: PropTypes.shape(programShape),
  corporateEndorsements: PropTypes.arrayOf(PropTypes.shape()),
  creditRedemptionOverview: PropTypes.string,
  creditValue: PropTypes.number,
  authoringOrganizations: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    key: PropTypes.string,
    logoImageUrl: PropTypes.string,
  })),
  isProgramEligibleForOneClickPurchase: PropTypes.bool.isRequired,
  overview: PropTypes.string.isRequired,
  hasOfacRestrictions: PropTypes.bool.isRequired,
  programDurationOverride: PropTypes.string,
  programOriginalPrice: PropTypes.number.isRequired,
  programPrice: PropTypes.number.isRequired,
  primarySubject: PropTypes.shape({
    name: PropTypes.string,
    slug: PropTypes.string,
  }).isRequired,
  staff: PropTypes.arrayOf(PropTypes.shape(InstructorCard.propTypes)).isRequired,
  expectedLearningItems: PropTypes.arrayOf(PropTypes.string),
  instructorOrdering: PropTypes.arrayOf(PropTypes.shape(InstructorCard.propTypes)),
  jobOutlookItems: PropTypes.arrayOf(PropTypes.string),
  uuid: PropTypes.string.isRequired,
  enrollButtonId: PropTypes.string.isRequired,
  video: videoShape,
  intl: intlShape.isRequired,
  userCurrency: currencyInfoShape,
  tracking: PropTypes.shape(),
  showPrice: PropTypes.bool,
  courseFocusId: PropTypes.string,
  retiringSoon: PropTypes.bool,
  subscriptionDetails: PropTypes.shape({
    isSubscriptionEnabled: PropTypes.bool,
    subscriptionStatus: PropTypes.string,
    // eslint-disable-next-line react/forbid-prop-types
    priceDetails: PropTypes.object,
  }),
  isSubscriptionLoading: PropTypes.bool,
};

BaseProgramMain.defaultProps = {
  faq: [],
  authoringOrganizations: [],
  corporateEndorsements: [],
  creditRedemptionOverview: '',
  expectedLearningItems: [],
  instructorOrdering: [],
  jobOutlookItems: [],
  video: {},
  tracking: {},
  userCurrency: {},
  creditValue: 0,
  showPrice: true,
  courseFocusId: '',
  linkedProgram: null,
  retiringSoon: false,
  programDurationOverride: '',
  isSubscriptionLoading: false,
  subscriptionDetails: {
    isSubscriptionEnabled: false,
    subscriptionStatus: '',
  },
};

const mapStateToProps = store => ({
  userCurrency: store.userCurrency || ({ ...defaultUserCurrency }),
});

const ProgramMain = connect(mapStateToProps)(BaseProgramMain);

export default track({
  component: 'experiment-program-details',
})(injectIntl(ProgramMain));
